import { formatMoneyShort } from '@/lib/format';

interface BudgetMeterProps {
  planned: number;
  budget: number;
  currency: string;
}

/**
 * Meter, not a chart: the fill carries severity (accent → warning → critical)
 * and the track is a lighter step of the same ramp so the state reads across
 * the whole bar. The percentage is labelled, so colour never stands alone.
 */
export function BudgetMeter({ planned, budget, currency }: BudgetMeterProps) {
  const ratio = budget > 0 ? planned / budget : 0;
  const percent = Math.round(ratio * 100);
  const fill =
    ratio > 1 ? 'var(--critical)' : ratio > 0.9 ? 'var(--warning)' : 'var(--accent)';
  const state =
    ratio > 1 ? 'Budget überschritten' : ratio > 0.9 ? 'Budget fast ausgeschöpft' : 'Im Budget';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="num text-sm font-semibold text-[var(--ink)]">{percent} %</span>
        <span className="num text-xs text-[var(--ink-3)]">
          {formatMoneyShort(planned, currency)} von {formatMoneyShort(budget, currency)}
        </span>
      </div>
      <div
        role="meter"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Budgetauslastung: ${percent} Prozent`}
        className="h-2.5 w-full overflow-hidden rounded-full"
        style={{ background: 'color-mix(in oklch, var(--accent) 16%, var(--surface-3))' }}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${Math.min(100, Math.max(ratio * 100, planned > 0 ? 2 : 0))}%`, background: fill }}
        />
      </div>
      <p className="text-xs text-[var(--ink-3)]">{state}</p>
    </div>
  );
}
